import { useEffect, useRef, useState } from 'react'
import mermaid from 'mermaid'
import { useSession } from '../store/session'
import { SpiderWeb, type SpiderGraph } from '../lib/spiderweb'

mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  // Spider-web-ish layout: ELK gives a more radial spread than the
  // default dagre when there's a single central root.
  flowchart: { curve: 'basis' },
})

interface BuildState {
  status: 'idle' | 'running' | 'done' | 'error'
  log: string[]
  graph?: SpiderGraph
  mermaidSrc?: string
  prompt?: string
  err?: string
}

export function Spider() {
  const session = useSession((s) => s.session)
  const [handle, setHandle] = useState(session?.handle ?? '')
  const [maxFollows, setMaxFollows] = useState(50)
  const [maxLikes, setMaxLikes] = useState(30)
  const [maxColikers, setMaxColikers] = useState(8)
  const [state, setState] = useState<BuildState>({ status: 'idle', log: [] })
  const [tab, setTab] = useState<'render' | 'mermaid' | 'json' | 'prompt'>('render')
  const renderRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!handle && session?.handle) setHandle(session.handle)
  }, [session, handle])

  useEffect(() => {
    if (!state.mermaidSrc || tab !== 'render' || !renderRef.current) return
    const el = renderRef.current
    el.innerHTML = ''
    mermaid
      .render(`spider-${Date.now()}`, state.mermaidSrc)
      .then(({ svg }) => {
        el.innerHTML = svg
      })
      .catch((e) => {
        el.innerHTML = `<pre class="text-rose-200 text-xs whitespace-pre-wrap">mermaid render failed: ${String(e)}</pre>`
      })
  }, [state.mermaidSrc, tab])

  async function run() {
    const h = handle.trim().replace(/^@/, '')
    if (!h) return
    setState({ status: 'running', log: [`starting build for @${h}`] })
    const web = new SpiderWeb(h)
    try {
      const graph = await web.buildFor({
        maxFollows,
        maxLikes,
        maxColikersPerPost: maxColikers,
        onProgress: (msg) =>
          setState((cur) => ({ ...cur, log: [...cur.log, msg] })),
      })
      const mermaidSrc = web.toMermaid()
      const prompt = web.toAiPrompt()
      setState((cur) => ({
        ...cur,
        status: 'done',
        graph,
        mermaidSrc,
        prompt,
      }))
    } catch (e) {
      setState((cur) => ({
        ...cur,
        status: 'error',
        err: (e as Error).message,
      }))
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // best-effort
    }
  }

  const ownHandle = session?.handle ?? ''
  const targetingSelf = handle.trim().toLowerCase() === ownHandle.toLowerCase()

  return (
    <main className="w-full p-4">
      <h1 className="mb-2 text-xl font-bold text-white">🕸 spider</h1>
      <p className="mb-3 max-w-2xl text-xs text-ink-300">
        Pulls a handle's follows + likes + the people who also liked those posts,
        builds a JSON graph, emits a Mermaid spider-web diagram, and produces a
        ChatGPT-ready prompt to render it as an artistic image.{' '}
        <span className="text-amber-200">
          Bluesky only returns likes for the logged-in account, so the handle is
          locked to @{ownHandle || '(no session)'}.
        </span>
      </p>

      <div className="mb-3 flex flex-wrap items-end gap-3 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-ink-400">handle</span>
          <input
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            className="glass-input w-56"
            placeholder="handle"
            disabled
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-ink-400">max follows</span>
          <input
            type="number"
            min={1}
            max={500}
            value={maxFollows}
            onChange={(e) => setMaxFollows(parseInt(e.target.value, 10) || 50)}
            className="glass-input w-20"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-ink-400">max likes</span>
          <input
            type="number"
            min={1}
            max={300}
            value={maxLikes}
            onChange={(e) => setMaxLikes(parseInt(e.target.value, 10) || 30)}
            className="glass-input w-20"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-ink-400">co-likers / post</span>
          <input
            type="number"
            min={0}
            max={50}
            value={maxColikers}
            onChange={(e) => setMaxColikers(parseInt(e.target.value, 10) || 8)}
            className="glass-input w-20"
          />
        </label>
        <button
          type="button"
          onClick={run}
          disabled={state.status === 'running' || !targetingSelf}
          className="glass-button"
          title={!targetingSelf ? 'getActorLikes only returns your own likes' : 'run build'}
        >
          {state.status === 'running' ? '⏳ building…' : '⚙ build spider web'}
        </button>
      </div>

      {state.log.length > 0 ? (
        <div className="mb-3 max-h-32 overflow-auto rounded-xl border border-white/10 bg-black/40 p-2 font-mono text-[11px] text-ink-200">
          {state.log.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      ) : null}

      {state.err ? (
        <div className="mb-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
          {state.err}
        </div>
      ) : null}

      {state.status === 'done' && state.graph ? (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
              {state.graph.nodes.length} nodes
            </span>
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
              {state.graph.edges.length} edges
            </span>
            {(['render', 'mermaid', 'json', 'prompt'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={
                  tab === t
                    ? 'rounded-md border border-[color:var(--accent-strong)] bg-white/10 px-2 py-0.5 text-white'
                    : 'rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-ink-200 hover:bg-white/10'
                }
              >
                {t}
              </button>
            ))}
            {tab !== 'render' ? (
              <button
                type="button"
                className="glass-button"
                onClick={() =>
                  copy(
                    tab === 'mermaid'
                      ? state.mermaidSrc ?? ''
                      : tab === 'prompt'
                        ? state.prompt ?? ''
                        : JSON.stringify(state.graph, null, 2),
                  )
                }
              >
                📋 copy {tab}
              </button>
            ) : null}
          </div>

          {tab === 'render' ? (
            <div
              ref={renderRef}
              className="overflow-auto rounded-xl border border-white/10 bg-black/40 p-3"
              style={{ minHeight: 320 }}
            />
          ) : null}
          {tab === 'mermaid' ? (
            <pre className="max-h-[60vh] overflow-auto rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] text-ink-100">
              {state.mermaidSrc}
            </pre>
          ) : null}
          {tab === 'json' ? (
            <pre className="max-h-[60vh] overflow-auto rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] text-ink-100">
              {JSON.stringify(state.graph, null, 2)}
            </pre>
          ) : null}
          {tab === 'prompt' ? (
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] text-ink-100">
              {state.prompt}
            </pre>
          ) : null}
        </>
      ) : null}
    </main>
  )
}
